import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EmailOcrPngFile, EmailOcrResult } from './email-ocr.service';

@Injectable({
  providedIn: 'root',
})
export class EmailApplicationBridgeService {
  private router = inject(Router);
  private http = inject(HttpClient);

  /**
   * Navigate to add-application with OCR data from email attachment
   */
  createApplicationFromEmail(ocrResult: EmailOcrResult, whiteLabelId?: number): void {
    // Store OCR data in sessionStorage for add-application to pick up
    const bridgeData = {
      source: 'email-attachment',
      originalAttachment: ocrResult.originalAttachment,
      pngFiles: ocrResult.pngFiles,
      ocrData: ocrResult.ocrData,
      uploadedFile: ocrResult.uploadedFile,
      parentPdfId: ocrResult.uploadedFile.originalPdf.id, // Kritičan za RunThrough i povezivanje
      whiteLabelId: whiteLabelId,
      timestamp: Date.now(),
    };

    // Store data with unique key
    const storageKey = `email-ocr-bridge-${Date.now()}`;
    sessionStorage.setItem(storageKey, JSON.stringify(bridgeData));

    // Navigate to add-application with bridge key
    this.router.navigate(['/v2/applications/add'], {
      queryParams: {
        emailOcr: storageKey,
      },
    });
  }

  /**
   * Backend upload of email attachments for a given email id/provider
   * Temporary helper to inspect response shape from BE
   */
  uploadEmailAttachments(
    emailId: string | number,
    provider: 'gmail' | 'outlook',
    attachmentIds: (string | number)[]
  ) {
    const url = `emails/${emailId}/attachments/upload?provider=${encodeURIComponent(provider)}`;
    const payload = { attachmentIds };
    return this.http.post<any>(url, payload);
  }

  /**
   * Get stored OCR data from email bridge (called by add-application)
   */
  getEmailOcrData(bridgeKey: string): any | null {
    try {
      const data = sessionStorage.getItem(bridgeKey);
      if (data) {
        // Clean up after retrieval
        sessionStorage.removeItem(bridgeKey);
        return JSON.parse(data);
      }
      return null;
    } catch (error) {
      console.error('Error retrieving email OCR bridge data:', error);
      return null;
    }
  }

  /**
   * Transform EmailOcrPngFile[] to format expected by OcrMatchDialog
   */
  transformPngFilesForDialog(pngFiles: EmailOcrPngFile[]): any[] {
    return pngFiles.map(png => ({
      id: png.id,
      filename: png.filename,
      location: png.location,
      size: png.size,
      type: png.type,
      category: 'ocr',
      parentId: png.parentId,
    }));
  }

  /**
   * Clean up old bridge data (call on app init)
   */
  cleanupOldBridgeData(): void {
    const keys = Object.keys(sessionStorage);
    const bridgeKeys = keys.filter(key => key.startsWith('email-ocr-bridge-'));
    const now = Date.now();
    const maxAge = 30 * 60 * 1000; // 30 minutes

    bridgeKeys.forEach(key => {
      try {
        const data = JSON.parse(sessionStorage.getItem(key) || '{}');
        if (data.timestamp && now - data.timestamp > maxAge) {
          sessionStorage.removeItem(key);
        }
      } catch {
        // Invalid data, remove it
        sessionStorage.removeItem(key);
      }
    });
  }
}
